import React, { useContext } from 'react';

import { Select } from '@aline/common';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CircleX, Undo2 } from 'lucide-react';
import type { OrphanRelationshipRow } from 'src/components';
import { AlineTooltip } from 'src/components';
import { getRelationshipLabel } from 'src/components/generic/SelectRelatedDocumentsWithRelationshipDialog';
import { CancelCircleIcon } from 'src/components/icons/CancelCircleIcon';
import { MagnifyingGlassIcon } from 'src/components/icons/MagnifyingGlassIcon';
import { UploadNewDocumentIcon } from 'src/components/icons/UploadNewDocumentIcon';
import { WarningCircleIcon } from 'src/components/icons/WarningCircleIcon';
import { Apollo } from 'src/gql/generated';
import { getOrphanRelationshipDisplay } from 'src/helpers';
import { useRelationshipActions } from 'src/hooks';

import { openOrphanResolution } from './OrphanResolutionDialogs';
import { DocumentGridReportIdContext } from './RepositoryContextProviders';
import { AIReasoningTooltip, useRelationship } from './repositoryDocumentNameCellHelpers';

interface OrphanRelationshipRowCellProps {
  classes: Record<
    | 'cell'
    | 'directionLabel'
    | 'documentName'
    | 'matchingDocumentPlaceholderIcon'
    | 'nameContainer'
    | 'orphanWarningText'
    | 'relationshipActions'
    | 'relationshipLabel',
    string
  >;
  row: OrphanRelationshipRow;
}

export const OrphanRelationshipRowCell = React.memo(function OrphanRelationshipRowCell({
  classes,
  row,
}: OrphanRelationshipRowCellProps): JSX.Element {
  const { relationship } = useRelationship(row);
  const repositoryId = useContext(DocumentGridReportIdContext);
  const actions = useRelationshipActions({
    repositoryId,
    row,
  });
  const orphanRelType = relationship?.childRelationshipType;
  const isChildDirection = row.orphanedRelationshipDirection === Apollo.RelationshipDirection.Child;
  const orphanLabelDirection: 'parent' | 'child' = isChildDirection ? 'parent' : 'child';
  const orphanBadgeLabel = isChildDirection ? 'CHILD' : 'PARENT';
  const hasAiReasoning = Boolean(row.orphanedRelationship.llmReasonsMarkdown);
  const isRejected = relationship?.review?.outcome === Apollo.ReviewOutcome.Rejected;
  const expectedDocumentName = row.orphanedRelationship.expectedDocumentName;
  /** `secondLineName` is the extracted name shown above the not-found text. */
  const { notFoundText, secondLineName } = getOrphanRelationshipDisplay({
    expectedDocumentName,
    missingSide: isChildDirection ? 'child' : 'parent',
  });

  const excludeDocumentId = isChildDirection
    ? (row.orphanedRelationship.parentDocumentId ?? undefined)
    : (row.orphanedRelationship.childDocumentId ?? undefined);

  const onResolve = async (docId: UUID): Promise<void> => {
    if (isChildDirection) {
      await actions.handleSetChildAndConfirm(docId, row.orphanedRelationship.id);
    } else {
      await actions.handleSetParentAndConfirm(docId, row.orphanedRelationship.id);
    }
  };

  const handleOpenDialog = (dialogType: 'select' | 'upload') => (event: React.MouseEvent) => {
    event.stopPropagation();
    openOrphanResolution({
      dialogType,
      excludeDocumentId,
      onResolve,
      sourceDocumentName: 'Document not found',
    });
  };

  return (
    <div className={classes.cell}>
      <div className={classes.nameContainer}>
        <Box
          sx={{
            alignItems: secondLineName ? 'flex-start' : 'center',
            display: 'flex',
            gap: 1,
          }}
        >
          <WarningCircleIcon
            className={classes.matchingDocumentPlaceholderIcon}
            height={14}
            width={14}
          />
          <OrphanRelationshipName
            classes={classes}
            notFoundText={notFoundText}
            secondLineName={secondLineName}
          />
        </Box>
        <Box
          className={classes.relationshipActions}
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 0.5,
          }}
        >
          <Chip
            className={classes.directionLabel}
            data-cy={Select.relationships.directionBadge}
            label={orphanBadgeLabel}
            size="small"
          />
          <Typography noWrap className={classes.relationshipLabel} variant="caption">
            {orphanRelType
              ? getRelationshipLabel({
                  direction: orphanLabelDirection,
                  type: orphanRelType,
                })
              : 'Related to'}
          </Typography>
          {hasAiReasoning && (
            <AIReasoningTooltip
              llmReasonsMarkdown={row.orphanedRelationship.llmReasonsMarkdown}
              relationshipId={row.orphanedRelationship.id}
            />
          )}
          <OrphanActions
            actions={actions}
            handleOpenDialog={handleOpenDialog}
            isRejected={isRejected}
            missingDirection={isChildDirection ? 'parent' : 'child'}
            repositoryId={repositoryId}
            row={row}
          />
        </Box>
      </div>
    </div>
  );
});

function OrphanRelationshipName({
  classes,
  notFoundText,
  secondLineName,
}: {
  classes: OrphanRelationshipRowCellProps['classes'];
  notFoundText: string;
  secondLineName: string | null;
}): JSX.Element {
  if (secondLineName) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
        <AlineTooltip arrow placement="top" title={secondLineName}>
          <Typography noWrap className={classes.documentName}>
            {secondLineName}
          </Typography>
        </AlineTooltip>
        <Typography noWrap className={classes.orphanWarningText}>
          {notFoundText}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography noWrap className={classes.orphanWarningText}>
      {notFoundText}
    </Typography>
  );
}

interface OrphanActionsProps {
  actions: ReturnType<typeof useRelationshipActions>;
  handleOpenDialog: (dialogType: 'select' | 'upload') => (event: React.MouseEvent) => void;
  isRejected: boolean;
  missingDirection: 'parent' | 'child';
  repositoryId: UUID | null;
  row: OrphanRelationshipRow;
}

function OrphanActions({
  actions,
  handleOpenDialog,
  isRejected,
  missingDirection,
  repositoryId,
  row,
}: OrphanActionsProps): JSX.Element {
  const theme = useTheme();

  if (isRejected) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 0.5,
        }}
      >
        <AlineTooltip arrow placement="top" title="Rejected. Click to undo.">
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <CircleX color={theme.palette.error.main} size={18} />
          </Box>
        </AlineTooltip>
        <AlineTooltip arrow placement="top" title="Rejected. Click to undo.">
          <span>
            <IconButton
              disabled={actions.undoing}
              size="medium"
              onClick={async event => {
                event.stopPropagation();
                await actions.handleUndoRelationship(row.orphanedRelationship.id);
              }}
            >
              <Undo2 size={16} />
            </IconButton>
          </span>
        </AlineTooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 0.5,
      }}
    >
      <AlineTooltip
        arrow
        placement="top"
        title={`Search repository for ${missingDirection} document`}
      >
        <span data-cy={Select.relationships.orphan.searchButton}>
          <IconButton
            disabled={!repositoryId || actions.settingParent || actions.settingChild}
            size="medium"
            onClick={handleOpenDialog('select')}
          >
            <MagnifyingGlassIcon height={16} width={16} />
          </IconButton>
        </span>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title={`Upload ${missingDirection} document`}>
        <span data-cy={Select.relationships.orphan.uploadButton}>
          <IconButton
            disabled={!repositoryId || actions.settingParent || actions.settingChild}
            size="medium"
            onClick={handleOpenDialog('upload')}
          >
            <UploadNewDocumentIcon height={16} width={16} />
          </IconButton>
        </span>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Reject relationship">
        <span data-cy={Select.relationships.orphan.rejectButton}>
          <IconButton
            disabled={actions.rejecting || actions.settingParent || actions.settingChild}
            size="medium"
            onClick={async event => {
              event.stopPropagation();
              await actions.handleRejectRelationship();
            }}
          >
            <CancelCircleIcon height={16} width={16} />
          </IconButton>
        </span>
      </AlineTooltip>
    </Box>
  );
}
